import { useState } from "react";
import imageManifest from "../../data/imageManifest.json";

/**
 * Manifest-driven <picture> (AVIF/WebP + compressed fallback), replacing
 * FallbackImage.jsx. `src` is the same logical path already used in
 * frontmatter/JSX (e.g. "/assets/spenden/wayfarer-2026-hero.png") —
 * generated by `npm run optimize-images` (scripts/optimize-images.js).
 *
 * No manifest entry (image not uploaded to public/ yet, per
 * image-inbox/README.md) renders the site's gradient placeholder
 * synchronously — no network request attempted at all, unlike the old
 * FallbackImage which still fired a request and waited for onError.
 * Pass `hideOnMissing` to render nothing instead, for spots where an
 * absent image should just collapse rather than show a placeholder block.
 *
 * `decorative` is the ONLY way to get an empty alt out of this component:
 * scripts/check-seo.js fails the build on any `alt=""` that isn't marked
 * `data-decorative`, so "no alt text yet" can never quietly ship as "this
 * image carries no meaning" (§ T-10).
 */
export default function ResponsiveImage({ src, alt, decorative = false, priority = false, sizes = "100vw", variant = "dark", hideOnMissing = false, className, style, "aria-hidden": ariaHidden }) {
  const [failed, setFailed] = useState(false);
  const entry = src ? imageManifest[src] : undefined;

  if (!entry || failed) {
    return hideOnMissing ? null : <div className={variant === "light" ? "tl-diagonal-light" : "tl-diagonal"} style={style} />;
  }

  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";

  return (
    <picture>
      {entry.avif.length > 0 && (
        <source type="image/avif" srcSet={entry.avif.map((v) => `${v.path} ${v.width}w`).join(", ")} sizes={sizes} />
      )}
      {entry.webp.length > 0 && (
        <source type="image/webp" srcSet={entry.webp.map((v) => `${v.path} ${v.width}w`).join(", ")} sizes={sizes} />
      )}
      <img
        src={src}
        alt={decorative ? "" : alt}
        {...(decorative && { "data-decorative": "true" })}
        width={entry.width}
        height={entry.height}
        loading={loading}
        decoding="async"
        fetchpriority={fetchPriority}
        aria-hidden={ariaHidden}
        onError={() => setFailed(true)}
        className={className}
        style={style}
      />
    </picture>
  );
}
